import { Controller } from "@hotwired/stimulus";

import logger from "~/lib/logger";

const log = logger({ name: "AutosuggestController" });

interface AutosuggestItem {
  id: string;
  value: string;
  label?: string;
  html: string;
  preview_html?: string | null;
  disabled: boolean;
  fields?: Record<string, string>;
}

interface AutosuggestResponse {
  success: boolean;
  items: AutosuggestItem[];
  error?: string;
}

type RefreshStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED";

const isInFlight = (status: RefreshStatus): boolean =>
  status === "QUEUED" || status === "RUNNING";

interface RefreshResponse {
  status: RefreshStatus;
  html: string | null;
}

/** How often to ask whether the refresh has finished. */
const REFRESH_POLL_INTERVAL = 2000;

/** Give up polling rather than hammer an endpoint that keeps failing. */
const REFRESH_POLL_MAX_FAILURES = 3;

export default class AutosuggestController extends Controller {
  static override targets = [
    "input",
    "listbox",
    "option",
    "selected",
    "status",
    "display",
    "value",
  ];

  static override values = {
    url: String,
    minChars: { type: Number, default: 1 },
    debounce: { type: Number, default: 200 },
    maxResults: { type: Number, default: 10 },
    allowCustom: { type: Boolean, default: false },
    loadingText: { type: String, default: "Searching..." },
    emptyText: { type: String, default: "No results found" },
    errorText: { type: String, default: "Error loading results" },
    initialQuery: { type: String, default: "" },
    noMatchHtml: { type: String, default: "" },
    emptyHtml: { type: String, default: "" },
    refreshUrl: { type: String, default: "" },
    lookupOnly: { type: Boolean, default: false },
    defaultValue: { type: String, default: "" },
    defaultTemplate: { type: String, default: "" },
    verifyDefaultValue: { type: Boolean, default: true },
    include: { type: Array, default: [] },
  };

  declare readonly inputTarget: HTMLInputElement;
  declare readonly listboxTarget: HTMLUListElement;
  declare readonly optionTargets: HTMLLIElement[];
  declare readonly selectedTarget: HTMLDivElement;
  declare readonly hasSelectedTarget: boolean;
  declare readonly statusTarget: HTMLDivElement;
  declare readonly hasStatusTarget: boolean;
  declare readonly displayTarget: HTMLSpanElement;
  declare readonly hasDisplayTarget: boolean;
  /** Carries the submitted value when the input holds a label instead. */
  declare readonly valueTarget: HTMLInputElement;
  declare readonly hasValueTarget: boolean;

  declare readonly urlValue: string;
  declare readonly minCharsValue: number;
  declare readonly debounceValue: number;
  declare readonly maxResultsValue: number;
  declare readonly allowCustomValue: boolean;
  declare readonly loadingTextValue: string;
  declare readonly emptyTextValue: string;
  declare readonly errorTextValue: string;
  declare readonly initialQueryValue: string;
  declare readonly noMatchHtmlValue: string;
  declare readonly emptyHtmlValue: string;
  declare readonly refreshUrlValue: string;
  declare readonly lookupOnlyValue: boolean;
  declare readonly defaultValueValue: string;
  /** Text for a blank field with a default, with "{value}" in place of it. */
  declare readonly defaultTemplateValue: string;
  declare readonly verifyDefaultValueValue: boolean;
  /** Names of other fields in the same form to send with each search. */
  declare readonly includeValue: string[];

  private debounceTimeout: number | null = null;
  private refreshTimeout: number | null = null;
  private isRefreshing = false;
  private refreshFailures = 0;
  private abortController: AbortController | null = null;
  private activeIndex = -1;
  private items: AutosuggestItem[] = [];
  private isOpen = false;
  private selectedLabel = "";
  private selectedPreviewHtml = "";

  override connect(): void {
    // Initial preview is rendered server-side, no need to set it here
    this.selectedLabel = this.inputTarget.value;
    if (this.hasValueTarget && this.hasSelectedTarget) {
      this.selectedPreviewHtml = this.selectedTarget.innerHTML.trim();
    }
    if (this.hasStatusTarget) {
      this.updateDisplay();
    }
    document.addEventListener("click", this.handleOutsideClick);
  }

  override disconnect(): void {
    document.removeEventListener("click", this.handleOutsideClick);

    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout);
    }
    this.stopRefreshing();
    this.abortController?.abort();
  }

  onInput(): void {
    // Hide selected preview when user starts typing
    this.hideSelected();

    const query = this.inputTarget.value.trim();

    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout);
    }

    if (this.lookupOnlyValue) {
      if (query) {
        this.debounceTimeout = window.setTimeout(() => {
          this.lookup(query, false);
        }, this.debounceValue);
      }
      return;
    }

    if (query.length < this.minCharsValue) {
      this.close();
      return;
    }

    this.debounceTimeout = window.setTimeout(() => {
      this.search(query);
    }, this.debounceValue);
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          this.activateNext();
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (this.isOpen) {
          this.activatePrevious();
        }
        break;

      case "Enter":
        if (this.isOpen && this.activeIndex >= 0) {
          event.preventDefault();
          this.selectActiveOption();
        }
        break;

      case "Escape":
        if (this.isOpen) {
          event.preventDefault();
          this.close();
        }
        break;

      case "Home":
        if (this.isOpen && this.items.length > 0) {
          event.preventDefault();
          this.setActiveIndex(0);
        }
        break;

      case "End":
        if (this.isOpen && this.items.length > 0) {
          event.preventDefault();
          this.setActiveIndex(this.items.length - 1);
        }
        break;

      case "Tab":
        if (this.isOpen) {
          this.close();
        }
        break;
    }
  }

  onFocus(): void {
    if (this.hasStatusTarget) {
      this.updateDisplay();
    }

    if (this.lookupOnlyValue) {
      return;
    }

    // The label of a key-valued field is not something the user can edit into
    // another answer, so hand them the list and let typing replace all of it.
    if (this.hasValueTarget) {
      this.inputTarget.select();
      if (this.minCharsValue === 0) {
        void this.search("");
      }
      return;
    }

    const query = this.inputTarget.value.trim();

    // Trigger initial search if configured and no value is set
    if (this.initialQueryValue && !this.inputTarget.value) {
      this.search(this.initialQueryValue);
      return;
    }

    if (query.length >= this.minCharsValue && this.items.length > 0) {
      this.open();
    }
  }

  onBlur(): void {
    window.setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.close();

        // Typing that picked nothing must not be left standing next to the
        // value it does not describe.
        if (this.hasValueTarget) {
          if (!this.inputTarget.value.trim()) {
            this.clearValue();
            return;
          }
          this.inputTarget.value = this.selectedLabel;
          this.renderPreview();
          return;
        }

        // A refresh in flight owns the preview until it settles, and clicking
        // its button blurs the input in browsers that don't focus buttons.
        if (this.isRefreshing) {
          return;
        }

        const value = this.inputTarget.value.trim();

        // If input is empty and defaultValue is set, lookup the default
        if (!value && this.defaultValueValue) {
          this.inputTarget.placeholder = this.defaultText(
            this.defaultValueValue,
          );
          if (this.verifyDefaultValueValue) {
            this.lookup(this.defaultValueValue, true);
          } else {
            this.hideSelected();
          }
          return;
        }

        // If input has a value, lookup to show preview
        if (value) {
          this.lookup(value, true);
        }
      }
    }, 150);
  }

  /**
   * Re-sync the options from the system they originate in, then look again.
   *
   * Offered from both no-match states, since neither can tell "no such record
   * exists" apart from "we have not synced it into our database yet".
   */
  async refreshEntities(): Promise<void> {
    if (!this.refreshUrlValue || this.isRefreshing) {
      return;
    }

    const query = this.inputTarget.value.trim();
    const inDropdown = this.isOpen;

    // Set before awaiting, so a blur landing in between knows to keep its
    // hands off the preview.
    this.isRefreshing = true;
    this.refreshFailures = 0;

    const started = await this.fetchRefresh("POST");
    if (!started || !this.element.isConnected) {
      this.stopRefreshing();
      return;
    }

    if (!isInFlight(started.status)) {
      await this.settleRefresh(query, inDropdown);
      return;
    }

    this.renderRefreshState(started, inDropdown);
    this.scheduleRefreshPoll(query, inDropdown);
  }

  private scheduleRefreshPoll(query: string, inDropdown: boolean): void {
    // Chained rather than an interval, so a slow poll can never overlap the
    // next one and settle the same refresh twice.
    this.refreshTimeout = window.setTimeout(() => {
      void this.pollRefresh(query, inDropdown);
    }, REFRESH_POLL_INTERVAL);
  }

  private async pollRefresh(query: string, inDropdown: boolean): Promise<void> {
    this.refreshTimeout = null;

    const data = await this.fetchRefresh("GET");
    if (!this.element.isConnected) {
      this.stopRefreshing();
      return;
    }

    if (!data) {
      this.refreshFailures += 1;
      if (this.refreshFailures >= REFRESH_POLL_MAX_FAILURES) {
        // Stop guessing and show what we can actually see right now.
        await this.settleRefresh(query, inDropdown);
        return;
      }
      this.scheduleRefreshPoll(query, inDropdown);
      return;
    }
    this.refreshFailures = 0;

    if (isInFlight(data.status)) {
      this.scheduleRefreshPoll(query, inDropdown);
      return;
    }

    if (data.status === "FAILED") {
      this.stopRefreshing();
      this.renderRefreshState(data, inDropdown);
      return;
    }

    await this.settleRefresh(query, inDropdown);
  }

  /** Show whatever the refreshed options now say about the current input. */
  private async settleRefresh(
    query: string,
    inDropdown: boolean,
  ): Promise<void> {
    this.stopRefreshing();

    // The refresh settles seconds later, by which time the field may have
    // moved on. Leave it alone rather than resurrect a stale answer.
    if (this.inputTarget.value.trim() !== query) {
      return;
    }

    if (inDropdown) {
      if (this.isOpen) {
        await this.search(query);
      }
      return;
    }

    await this.lookup(
      query || (this.verifyDefaultValueValue ? this.defaultValueValue : ""),
      true,
    );
  }

  private async fetchRefresh(
    method: "GET" | "POST",
  ): Promise<RefreshResponse | null> {
    try {
      const response = await fetch(this.refreshUrlValue, {
        method,
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as RefreshResponse;
    } catch (error) {
      log.error("Entity refresh failed", error);
      return null;
    }
  }

  private renderRefreshState(data: RefreshResponse, inDropdown: boolean): void {
    if (!data.html) {
      return;
    }

    if (inDropdown) {
      // Don't force a dropdown the user has since dismissed back open.
      if (this.isOpen) {
        this.renderNotice(data.html);
      }
      return;
    }
    this.showSelected(data.html, false);
  }

  private stopRefreshing(): void {
    if (this.refreshTimeout !== null) {
      window.clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    this.isRefreshing = false;
  }

  /**
   * Lookup the given value against the search endpoint without opening the
   * dropdown. Used to verify typed input on blur and show preview.
   */
  private async lookup(value: string, showUnmatched: boolean): Promise<void> {
    this.abortController?.abort();

    if (!value) {
      this.hideSelected();
      return;
    }

    this.abortController = new AbortController();
    this.inputTarget.classList.add("opacity-75");

    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("q", value);
      url.searchParams.set("code_lookup", "true");

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AutosuggestResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Unknown error");
      }

      // Input may have changed while the request was in-flight
      const currentValue = this.inputTarget.value.trim();
      if (currentValue !== value && value !== this.defaultValueValue) {
        return;
      }

      const match = data.items.find((item) => item.value === value);
      if (match) {
        this.showSelected(match.html, true);
      } else if (showUnmatched) {
        // Use server-rendered no-match HTML if provided
        if (this.noMatchHtmlValue) {
          this.showSelected(this.noMatchHtmlValue, false);
        } else {
          this.hideSelected();
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      log.error("Lookup failed", error);
    } finally {
      this.inputTarget.classList.remove("opacity-75");
    }
  }

  private showSelected(html: string, matched = true): void {
    if (this.hasStatusTarget) {
      this.showStatus(html);
      return;
    }
    if (!this.hasSelectedTarget) return;

    this.selectedTarget.innerHTML = html;
    this.selectedTarget.querySelectorAll(".js-code-display")?.forEach((el) => {
      el.remove();
    });
    this.selectedTarget.classList.remove("hidden");

    // Toggle styling based on match status
    const unmatchedClasses =
      this.selectedTarget.dataset.unmatchedClass?.split(" ") ?? [];
    const matchedClasses =
      this.selectedTarget.dataset.matchedClass?.split(" ") ?? [];

    if (matched) {
      this.selectedTarget.classList.remove(...unmatchedClasses);
      this.selectedTarget.classList.add(...matchedClasses);
    } else {
      this.selectedTarget.classList.remove(...matchedClasses);
      this.selectedTarget.classList.add(...unmatchedClasses);
    }

    // Remove border radius bottom left and right to align
    // the selected item box neatly
    this.inputTarget.classList.add("rounded-br-none", "rounded-bl-none");
  }

  /** Show only the status icon of the preview, at the end of the input. */
  private showStatus(html: string): void {
    const preview = document.createElement("template");
    preview.innerHTML = html;
    const status = preview.content.querySelector(".js-status-icon");
    this.statusTarget.replaceChildren(...(status ? [status] : []));
    this.updateDisplay();
  }

  /** Show the value and the label of its match over the blurred input. */
  private updateDisplay(): void {
    if (!this.hasDisplayTarget) return;

    const label =
      this.statusTarget.querySelector<HTMLElement>("[data-label]")?.dataset
        .label;
    const value = this.inputTarget.value.trim();
    const text = value
      ? `${value} - ${label}`
      : this.defaultTemplateValue && this.defaultValueValue
        ? this.defaultText(`${this.defaultValueValue}: ${label}`)
        : "";
    const show = Boolean(
      label && text && document.activeElement !== this.inputTarget,
    );

    this.displayTarget.textContent = show ? text : "";
    this.displayTarget.classList.toggle("invisible", !show);
    this.inputTarget.classList.toggle("text-transparent", show);
    this.inputTarget.classList.toggle("placeholder:text-transparent", show);
    if (show) {
      // Line the text up with the text of the input it covers.
      const style = window.getComputedStyle(this.inputTarget);
      this.displayTarget.style.font = style.font;
      this.displayTarget.style.paddingLeft = `${
        parseFloat(style.borderLeftWidth) + parseFloat(style.paddingLeft)
      }px`;
    }
  }

  /** The text for a blank field whose default is `value`. */
  private defaultText(value: string): string {
    return this.defaultTemplateValue
      ? this.defaultTemplateValue.replace("{value}", value)
      : value;
  }

  private hideSelected(): void {
    if (this.hasStatusTarget) {
      this.showStatus("");
      return;
    }
    if (!this.hasSelectedTarget) return;

    this.selectedTarget.innerHTML = "";
    this.selectedTarget.classList.add("hidden");

    // Reset to unmatched styling for next time
    const unmatchedClasses =
      this.selectedTarget.dataset.unmatchedClass?.split(" ") ?? [];
    const matchedClasses =
      this.selectedTarget.dataset.matchedClass?.split(" ") ?? [];
    this.selectedTarget.classList.remove(...matchedClasses);
    this.selectedTarget.classList.add(...unmatchedClasses);

    this.inputTarget.classList.remove("rounded-br-none", "rounded-bl-none");
  }

  private async search(query: string): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.inputTarget.classList.add("opacity-75");

    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("q", query);
      this.includeFields(url);

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AutosuggestResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Unknown error");
      }

      this.items = data.items;
      this.renderItems();
      this.open();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      log.error("Search failed", error);
      this.showError();
    } finally {
      this.inputTarget.classList.remove("opacity-75");
    }
  }

  private renderItems(): void {
    this.listboxTarget.innerHTML = "";
    this.activeIndex = -1;

    if (this.items.length === 0) {
      this.renderEmptyState();
      return;
    }

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      if (!item) continue;

      const li = document.createElement("li");

      li.id = item.id;
      li.className = [
        "autosuggest-option",
        "px-3 py-2 cursor-pointer",
        "hover:bg-gray-100",
        "focus:bg-blue-100 focus:outline-none",
        item.disabled ? "opacity-50 cursor-not-allowed" : "",
      ]
        .filter(Boolean)
        .join(" ");
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      if (item.disabled) {
        li.setAttribute("aria-disabled", "true");
      }
      li.dataset.autosuggestTarget = "option";
      li.dataset.value = item.value;
      li.dataset.index = String(i);

      li.innerHTML = item.html;

      li.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!item.disabled) {
          this.selectItem(i);
        }
      });

      li.addEventListener("mouseenter", () => {
        this.setActiveIndex(i);
      });

      li.addEventListener("touchend", (e) => {
        e.preventDefault();
        if (!item.disabled) {
          this.selectItem(i);
        }
      });

      this.listboxTarget.appendChild(li);
    }
  }

  private renderEmptyState(): void {
    if (this.emptyHtmlValue) {
      this.renderNotice(this.emptyHtmlValue);
      return;
    }

    const li = document.createElement("li");
    li.className = "px-3 py-2 c-soft text-sm";
    li.setAttribute("role", "option");
    li.setAttribute("aria-disabled", "true");
    li.textContent = this.emptyTextValue;
    this.listboxTarget.appendChild(li);
  }

  /**
   * Replace the dropdown with a single server-rendered informational row.
   *
   * Unlike the plain-text empty state this can hold a button, so it is not an
   * option: announcing it as a disabled one would hide its contents.
   */
  private renderNotice(html: string): void {
    this.listboxTarget.innerHTML = "";
    const li = document.createElement("li");
    li.className = "px-3 py-2 c-soft text-sm";
    li.setAttribute("role", "presentation");
    li.innerHTML = html;
    this.listboxTarget.appendChild(li);
    this.open();
  }

  private showError(): void {
    this.listboxTarget.innerHTML = "";
    const li = document.createElement("li");
    li.className = "px-3 py-2 text-red-500 text-sm";
    li.setAttribute("role", "option");
    li.setAttribute("aria-disabled", "true");
    li.textContent = this.errorTextValue;
    this.listboxTarget.appendChild(li);
    this.open();
  }

  private selectItem(index: number): void {
    const item = this.items[index];
    if (!item || item.disabled) return;

    this.selectedLabel = item.label || item.value;
    this.inputTarget.value = this.selectedLabel;

    if (this.hasValueTarget) {
      this.valueTarget.value = item.value;
      this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
      this.selectedPreviewHtml = item.preview_html ?? "";
      this.renderPreview();
    } else {
      this.showSelected(item.preview_html ?? item.html);
    }

    this.fillFields(item);
    this.close();

    // Dispatch events
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));

    const customEvent = new CustomEvent("autosuggest:select", {
      bubbles: true,
      detail: { item },
    });
    this.element.dispatchEvent(customEvent);
  }

  /** Empties the submitted value once the input holding its label is emptied. */
  private clearValue(): void {
    this.selectedLabel = "";
    this.selectedPreviewHtml = "";
    this.inputTarget.value = "";
    this.renderPreview();
    if (this.valueTarget.value) {
      this.valueTarget.value = "";
      this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  /** Shows the preview of the picked item of a key-valued input, if it has one. */
  private renderPreview(): void {
    if (this.selectedPreviewHtml) {
      this.showSelected(this.selectedPreviewHtml);
    } else {
      this.hideSelected();
    }
  }

  private formField(name: string): Element | RadioNodeList | null {
    return this.inputTarget.closest("form")?.elements.namedItem(name) ?? null;
  }

  private includeFields(url: URL): void {
    for (const name of this.includeValue) {
      const field = this.formField(name);
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement ||
        field instanceof HTMLSelectElement
      ) {
        url.searchParams.set(name, field.value);
      }
    }
  }

  private fillFields(item: AutosuggestItem): void {
    if (!item.fields) return;

    for (const [name, value] of Object.entries(item.fields)) {
      const field = this.formField(name);
      if (field instanceof HTMLInputElement) {
        field.value = value;
      }
    }
  }

  private selectActiveOption(): void {
    if (this.activeIndex >= 0 && this.activeIndex < this.items.length) {
      this.selectItem(this.activeIndex);
    }
  }

  private activateNext(): void {
    const nextIndex = this.activeIndex + 1;
    if (nextIndex < this.items.length) {
      this.setActiveIndex(nextIndex);
    } else {
      this.setActiveIndex(0);
    }
  }

  private activatePrevious(): void {
    const prevIndex = this.activeIndex - 1;
    if (prevIndex >= 0) {
      this.setActiveIndex(prevIndex);
    } else {
      this.setActiveIndex(this.items.length - 1);
    }
  }

  private setActiveIndex(index: number): void {
    if (this.activeIndex >= 0) {
      const currentOption = this.optionTargets[this.activeIndex];
      if (currentOption) {
        currentOption.classList.remove("bg-blue-100");
        currentOption.setAttribute("aria-selected", "false");
      }
    }

    this.activeIndex = index;

    if (index >= 0 && index < this.optionTargets.length) {
      const newOption = this.optionTargets[index];
      if (newOption) {
        newOption.classList.add("bg-blue-100");
        newOption.setAttribute("aria-selected", "true");
        this.inputTarget.setAttribute("aria-activedescendant", newOption.id);
        this.scrollOptionIntoView(newOption);
      }
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant");
    }
  }

  private scrollOptionIntoView(option: HTMLElement): void {
    const listbox = this.listboxTarget;
    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;
    const listboxScrollTop = listbox.scrollTop;
    const listboxHeight = listbox.clientHeight;

    if (optionTop < listboxScrollTop) {
      listbox.scrollTop = optionTop;
    } else if (optionBottom > listboxScrollTop + listboxHeight) {
      listbox.scrollTop = optionBottom - listboxHeight;
    }
  }

  private open(): void {
    if (this.isOpen) return;

    this.isOpen = true;
    this.listboxTarget.classList.remove("hidden");
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  private close(): void {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.listboxTarget.classList.add("hidden");
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
    this.activeIndex = -1;

    this.optionTargets.forEach((option) => {
      option.classList.remove("bg-blue-100");
      option.setAttribute("aria-selected", "false");
    });
  }

  private handleOutsideClick = (event: Event): void => {
    if (!this.element.contains(event.target as Node)) {
      this.close();
    }
  };
}
